<template>
    <div class="relative overflow-hidden py-20 sm:py-32">
        <!-- decorative gradient blobs -->
        <div class="pointer-events-none absolute -top-24 -left-24 -z-10 h-96 w-96 rounded-full bg-primary-200 opacity-40 blur-3xl"
            aria-hidden="true"></div>
        <div class="pointer-events-none absolute top-1/3 -right-24 -z-10 h-[28rem] w-[28rem] rounded-full bg-secondary-200 opacity-40 blur-3xl"
            aria-hidden="true"></div>
        <div class="pointer-events-none absolute bottom-0 left-1/3 -z-10 h-72 w-72 rounded-full bg-accent-200 opacity-30 blur-3xl"
            aria-hidden="true"></div>

        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div class="lg:grid lg:grid-cols-12 lg:gap-x-8 lg:gap-y-20">
                <div class="relative z-10 mx-auto max-w-2xl lg:col-span-7 lg:max-w-none lg:pt-6 xl:col-span-6">
                    <span
                        class="mb-4 inline-flex items-center gap-2 rounded-full bg-primary-50 px-4 py-1.5 text-sm font-semibold text-primary-700 ring-1 ring-primary-600/10">
                        {{ data.subtitle }}
                    </span>
                    <h1 class="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
                        {{ data.titleStart }}
                        <span
                            class="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">{{
                            data.titleHighlight }}</span>
                        {{ data.titleEnd }}
                    </h1>
                    <p class="mt-6 text-lg text-slate-600">
                        {{ data.description }}
                    </p>
                    <div class="mt-8 flex flex-wrap gap-x-6 gap-y-4">
                        <Button variant="primary" size="sm" :href="data.buttonLink"
                            class="transition-all hover:shadow-lg hover:shadow-primary-500/25 active:scale-[0.98]"
                            @click="bookDemoModal = true">
                            <span class="text-sm font-semibold text-white">{{ data.buttonText }}</span>
                        </Button>

                        <Dialog v-model:visible="bookDemoModal" modal header="Request a Demo" :style="{ width: '50rem' }">
                            <div class="flex items-center gap-4 mb-4">
                                <label for="email" class="font-semibold w-24">email</label>
                                <InputText v-model="email" id="email" class="flex-auto" autocomplete="on" />
                            </div>
                            <div class="flex justify-end gap-2">
                                <Button type="button" label="Cancel" severity="secondary"
                                    @click="bookDemoModal = false"></Button>
                                <Button type="button" label="Request" @click="requestDemo"></Button>
                            </div>
                        </Dialog>
                        <VideoButton />
                    </div>

                    <dl class="mt-14 grid grid-cols-3 gap-6 border-t border-slate-200 pt-8 max-w-md">
                        <div v-for="stat in trustStats" :key="stat.name">
                            <dt class="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{{ stat.value }}</dt>
                            <dd class="mt-1 text-sm text-slate-500">{{ stat.name }}</dd>
                        </div>
                    </dl>
                </div>
                <div class="relative mt-10 sm:mt-20 lg:col-span-5 lg:row-span-2 lg:mt-0 xl:col-span-6">
                    <div
                        class="-mx-4 h-[448px] [mask-image:linear-gradient(to_bottom,white_60%,transparent)] px-9 sm:mx-0 lg:absolute lg:-inset-x-10 lg:-top-10 lg:-bottom-20 lg:h-auto lg:px-0 lg:pt-10 xl:-bottom-32">
                        <div class="relative aspect-[266/400] mx-auto max-w-[366px]">
                            <div
                                class="absolute -inset-6 -z-10 rounded-full border-2 border-dashed border-primary-300 animate-spin-slower">
                            </div>
                            <span class="absolute -top-4 -right-2 z-10 size-4 rounded-full bg-accent-400"
                                aria-hidden="true"></span>
                            <span class="absolute -bottom-2 -left-4 z-10 size-6 rounded-full bg-secondary-300"
                                aria-hidden="true"></span>
                            <img src="/images/home/header.jpg"
                                class="absolute inset-0 h-full w-full object-cover object-center rounded-full ring-4 ring-white shadow-xl" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { defineProps } from 'vue';
import VideoButton from '@/components/buttons/VideoButton.vue';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import { requestDemoService } from '@/services/requestDemoService';
import { ref } from 'vue';

const props = defineProps(['title', 'image']);
const bookDemoModal = ref(false);
const email = ref('');

const data = {
    subtitle: 'Welcome to ' + (props.title || 'Sultana Vera'),
    titleStart: 'Master Arabic & The',
    titleHighlight: 'Quran',
    titleEnd: 'with Native Tutors',
    description: 'Join us and experience an enriching journey through our online Quran, Arabic, and Islamic studies programs. Our Native Arabic tutors proficient in English.',
    buttonText: 'Book a Free Demo',
    buttonLink: '#'
};

const trustStats = [
    { name: 'Students enrolled', value: '1M+' },
    { name: 'Courses available', value: '500+' },
    { name: 'Languages supported', value: '5' },
];

const image = {
    alt: 'Image of a person studying Quran - Photo by Furkan Dil'
};

const requestDemo = () => {
    console.log('Requesting demo with email:', email.value);
    requestDemoService({ email: email.value })
        .then(() => {
            bookDemoModal.value = false;
        })
        .catch(() => {
            alert('There was an error processing your request. Please try again later.');
        });
};
</script>
